/**
 * Copyright (C), 2012-2021, Sichuan Xiaoka Technology Co., Ltd.
 * axios拦截器
 * */
import axios from 'axios'
import axiosMethods from './axios-methods'
import Vue from 'vue'
import store from '../store/index'
import router from '../router/index'
import QS from 'qs'
import utils from '@/assets/js/utils.js'
import { getDeviceFingerprint, aesDecrypt as aesDecryptFn, aesEncryptStr } from '@/assets/js/security.js'

// 开发环境始终使用 devServer 代理（baseURL 为空，使用相对路径）
// 生产环境使用当前域名
const getBaseURL = () => {
   if (process.env.NODE_ENV === 'production') {
      return window.location.origin
   }
   // 开发环境：无论 localhost 还是局域网访问，都使用 devServer 代理
   return ''
}

// 配置 axios 默认值
axios.defaults.baseURL = getBaseURL()
axios.defaults.timeout = 30000 // 30秒超时

// 添加网络诊断日志
console.log('=== Axios 配置 ===')
console.log('当前hostname:', window.location.hostname)
console.log('NODE_ENV:', process.env.NODE_ENV)
console.log('baseURL:', axios.defaults.baseURL)
console.log('==================')

// IP信息已改为后端自动获取（ip2region离线库），前端不再调用第三方IP API
// 预加载设备指纹（异步，启动时就计算好）
let _deviceFingerprintCache = null
getDeviceFingerprint().then(fp => { _deviceFingerprintCache = fp })

Vue.prototype.$axios = {
   get: axiosMethods.getAxios,
   put: axiosMethods.putAxios,
   post: axiosMethods.postAxios,
   delete: axiosMethods.deleteAxios
}

const userAgent = navigator.userAgent
const { browser, os } = utils.parseUserAgent(userAgent)

// 定义常量
const NO_TOKEN_URLS = ['/api/v1/manage/login', '/api/v1/manage/login/company', '/api/v1/manage/login/pubkey', '/api/v1/manage/login/secure']
const NOT_QS_URLS = ['http://up-z1.qiniu.com', 'https://upload-z1.qbox.me', 'https://up-z1.qiniup.com', '/api/v1/common/excel/read/import', '/api/v1/manage/hitchRide/helloBike/priceRule']
const NEED_QS_METHODS = ['post', 'put']

// 不加密的 URL 前缀（登录、第三方、回调等）
const NO_ENCRYPT_URL_PREFIXES = [
   '/api/v1/manage/login',
   'http://up-z1.qiniu.com',
   'https://upload-z1.qbox.me',
   'https://up-z1.qiniup.com',
   '/api/v1/pay/',
   '/api/v1/open/',
   '/api/v1/common/captcha',
   '/api/v1/common/connect',
   '/api/v1/common/title',
   '/api/v1/common/syncMenu',
   '/api/v1/common/h5',
]

/**
 * 判断该 URL 是否需要加密
 */
function shouldEncrypt(url) {
   if (!url) return false
   return !NO_ENCRYPT_URL_PREFIXES.some(prefix => url.startsWith(prefix))
}

/**
 * 获取当前用户的 Session AES 密钥（Base64 → Uint8Array）
 */
function getSessionAesKeyBytes() {
   try {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo'))
      if (!userInfo || !userInfo.sessionAesKey) return null
      const raw = atob(userInfo.sessionAesKey)
      return Uint8Array.from(raw, c => c.charCodeAt(0))
   } catch (e) {
      return null
   }
}

/**
 * 获取当前用户的 sessionId（从 token 或 userInfo）
 */
function getSessionId() {
   try {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo'))
      return userInfo && userInfo.sessionId ? userInfo.sessionId : null
   } catch (e) {
      return null
   }
}

// 请求拦截器
axios.interceptors.request.use(async function (config) {
   // 增加token
   let userInfo = JSON.parse(sessionStorage.getItem('userInfo'))
   if (userInfo?.token && !NO_TOKEN_URLS.includes(config.url)) {
      config.headers.common['token'] = userInfo.token
   }

   // 浏览器和系统信息（后端也会自动解析User-Agent，此处作为备用）
   if (config.method !== 'get') {
      config.headers.common['browser'] = `${browser.name}/${browser.version}`
      config.headers.common['os'] = `${os.name}/${os.version}/${os.bit}`
   }

   // 设备指纹（用于服务端设备绑定验证，防止token在其他设备上使用）
   if (_deviceFingerprintCache) {
      config.headers.common['X-Device-Fingerprint'] = _deviceFingerprintCache
   }

   // version header 仅用于非API请求，API版本路由通过URL路径(/api/v1/)控制
   // 避免触发后端VersionStorage版本路由导致空响应
   // config.headers.common['version'] = window.configure.version

   // 参数处理 + 全接口 AES 加密
   const encKeyBytes = getSessionAesKeyBytes()
   const encSessionId = getSessionId()
   if (NEED_QS_METHODS.includes(config.method) && !NOT_QS_URLS.includes(config.url)) {
      if (encKeyBytes && encSessionId && shouldEncrypt(config.url)) {
         // 加密模式：将参数序列化为 JSON 后 AES 加密，以 {"enc":"..."} 形式发送
         const plainBody = typeof config.data === 'string'
            ? config.data
            : JSON.stringify(config.data || {})
         const encryptedBody = await aesEncryptStr(plainBody, encKeyBytes)
         config.data = JSON.stringify({ enc: encryptedBody })
         config.headers.common['Content-Type'] = 'application/json;charset=UTF-8'
         config.headers.common['X-Enc-Session'] = encSessionId
      } else {
         // 非加密模式或无密钥：使用原有 QS 序列化
         config.data = QS.stringify(config.data)
      }
   } else if (encKeyBytes && encSessionId && shouldEncrypt(config.url)) {
      // GET 请求：只发送 X-Enc-Session 头（不加密 URL 参数，避免 URL 长度限制）
      config.headers.common['X-Enc-Session'] = encSessionId
   }

   return config
}, function (error) {
   return Promise.reject(error)
})

// 需要强制退出登录的错误码集合
const FORCE_LOGOUT_CODES = [
   50015,   // 账号已在其他设备登录（旧码，兼容保留）
   90005,   // 登录状态已失效，请重新登录
   90006,   // 登录环境发生变化（设备指纹不匹配）
   90007,   // 账号已在其他设备登录（新码）
   90008,   // 登录状态超时
]

// 登录加密降级静默码：这些错误码是正常降级流程，不向用户弹出错误提示
const SILENT_LOGIN_CODES = [90001, 90003]

// Add a response interceptor 响应
axios.interceptors.response.use(async function (response) {
   // 全接口加密响应解密：后端返回 {"enc":"Base64密文"} 时透明解密
   if (response.data && response.data.enc && typeof response.data.enc === 'string') {
      try {
         const keyBytes = getSessionAesKeyBytes()
         if (keyBytes) {
            const decryptedJson = await aesDecryptFn(response.data.enc, keyBytes)
            response.data = JSON.parse(decryptedJson)
         }
      } catch (e) {
         // 解密失败：继续使用原始响应（降级）
      }
   }
   if (FORCE_LOGOUT_CODES.includes(response.data.code)) {
      // 强制退出登录，显示详细原因
      Vue.prototype.$message.closeAll()
      Vue.prototype.$message.error(response.data.msg || '登录状态已失效，请重新登录')
      setTimeout(() => { store.dispatch('acLogout', '') }, 1500)
      return response.data
   } else if (SILENT_LOGIN_CODES.includes(response.data.code)) {
      // 登录加密凭证相关错误，属于正常降级流程，静默处理不弹窗
      return response.data
   } else if (response.data.code && response.data.code !== 1 && response.data.code !== 50030 && response.data.code !== 50200) {
      // 当出现错误码时，根据后端提供的错误码提示相应文案，并返回false
      Vue.prototype.$message.error(response.data.msg)
   }
   return response.data
}, function (error) {
   if (!error.response) {
      Vue.prototype.$message.error('网络连接异常，请检查网络后重试')
      return Promise.reject(error)
   }
   // 401-token过期:返回登录页
   if (error.response.status === 401) {
      Vue.prototype.$message.closeAll()
      Vue.prototype.$message.error(`错误状态码：${error.response.status}; 登录已经过期，请重新登录！`);
      store.dispatch('acLogout', '')
   }
   // 410-没有当前接口权限
   if (error.response.status === 410) {
      Vue.prototype.$message.closeAll()
      Vue.prototype.$alert("无操作权限！", "权限提示", {
         confirmButtonText: "确定",
         showClose: false,
         callback: () => {
            router.replace('/')
            router.go(0)
         }
      });
   }
   return Promise.reject(error)
})
